import { getApiErrorDetail } from "../../utils/idempotency";

/**
 * Always coerce a value into a safe React text child.
 * Prevents "Objects are not valid as a React child" and removesChild crashes
 * that follow when the tree tries to reconcile bad children.
 */
export function toDisplayString(value, fallback = "") {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((v) => toDisplayString(v)).filter(Boolean).join("; ") || fallback;
  }
  if (typeof value === "object") {
    if (typeof value.message === "string" && value.message) return value.message;
    if (typeof value.detail === "string" && value.detail) return value.detail;
    if (typeof value.code === "string" && typeof value.hint === "string") {
      return `${value.code}: ${value.hint}`;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return fallback || "Unexpected error";
    }
  }
  return fallback;
}

/**
 * Coerce an axios error's ``response.data.detail`` into a display string.
 *
 * The backend returns structured error bodies like
 * ``{ code, message, hint, steps, request_id }`` for many failures. Rendering that object
 * directly as a React child crashes with "Objects are not valid as a React child".
 */
export function bankApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail === undefined || detail === null || detail === "") {
    return toDisplayString(error?.message) || undefined;
  }
  return toDisplayString(getApiErrorDetail(error)) || undefined;
}

/** Human-readable text for field-error codes from employee_banking_validators. */
const FIELD_ERROR_MESSAGES = {
  account_number_required: "Account number is required.",
  account_number_format_invalid:
    "Account number must be 6-22 letters/digits (spaces and dashes are fine).",
  account_number_duplicate_across_employees:
    "This bank account number is already registered to another employee. You must enter a different account number.",
  ifsc_required: "IFSC code is required.",
  ifsc_format_invalid: "IFSC format is invalid — expected e.g. HDFC0001234.",
  account_type_invalid: "Choose a valid account type.",
  payment_method_invalid: "Choose a valid payment method.",
  upi_vpa_format_invalid: "UPI VPA must look like name@bank.",
  micr_format_invalid: "MICR code must be exactly 9 digits.",
};

/**
 * Parses structured ``{message, errors: [[field, code], ...]}`` validation bodies.
 * Returns null when the error body isn't in that shape.
 */
export function bankFieldErrors(error) {
  const detail = error?.response?.data?.detail;
  const errors = detail?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;

  const metaMsg = toDisplayString(detail?.metadata?.message || detail?.message);
  const fieldErrors = {};
  const messages = [];
  for (const entry of errors) {
    const [field, code] = Array.isArray(entry) ? entry : [entry?.field, entry?.code];
    let human = FIELD_ERROR_MESSAGES[code] || toDisplayString(code) || "Invalid value.";
    if (
      field === "account_number"
      && code === "account_number_duplicate_across_employees"
      && metaMsg
    ) {
      human = metaMsg;
    }
    if (field) fieldErrors[field] = human;
    messages.push(human);
  }
  return {
    fieldErrors,
    summary: metaMsg || messages.join(" "),
  };
}
